import * as React from 'react';
/**
 * When a screen counts as mobile: phones, portrait tablets and phones held sideways.
 * Same rule as `$mobile-query` in _global.scss (px on purpose: rem in a media query is the
 * browser's 16px, not the UI font size). Use it for `matchMedia`; in styles prefer
 * `html[data-uxp-viewport="mobile"]`.
 * @export
 */
export const MOBILE_MEDIA_QUERY = '(max-width: 767.98px), (orientation: portrait) and (max-width: 1279.98px), (max-height: 479.98px)';
// One media query listener shared by every component using the hook.
const listeners = new Set();
let mediaQuery = null;
const subscribe = (listener) => {
    if (!mediaQuery) {
        mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
        mediaQuery.addEventListener('change', () => listeners.forEach(l => l()));
    }
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};
const getSnapshot = () => window.matchMedia(MOBILE_MEDIA_QUERY).matches;
/**
 * True when the screen uses the mobile layout right now, for code outside React rendering
 * (event handlers, utilities). In components use `useIsMobileLayout()` so they update on rotation.
 * @export
 */
export const isMobileLayout = () => typeof window !== 'undefined' && getSnapshot();
// `html[data-uxp-viewport="mobile" | "desktop"]` drives the phone styles. It is kept up to date as
// soon as uxp loads, so every page gets it, in the app shell or not.
const applyViewportFlag = () => document.documentElement.setAttribute('data-uxp-viewport', getSnapshot() ? 'mobile' : 'desktop');
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    applyViewportFlag();
    window.matchMedia(MOBILE_MEDIA_QUERY).addEventListener('change', applyViewportFlag);
}
/**
 * True when the screen uses the mobile layout (phones, portrait tablets, phones held sideways).
 * Updates when the screen size or orientation changes.
 *
 * @example
 * ```
 * const isMobile = useIsMobileLayout();
 * return isMobile ? <CompactList /> : <FullTable />;
 * ```
 * @export
 */
const useIsMobileLayout = () => React.useSyncExternalStore(subscribe, getSnapshot, () => false);
/**
 * Same as `useIsMobileLayout()`. Kept for pages written before uxp set
 * `html[data-uxp-viewport]` by itself (it does now, on every page).
 * @export
 */
export const useViewportFlag = () => useIsMobileLayout();
export default useIsMobileLayout;
